import { useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

function WajimaNuriJsonLd() {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/portfolio/wajima-nuri#collection`,
      name: '輪島塗 | KAZU.PHOTO – 石川県 伝統工芸ドキュメンタリー写真',
      description: '石川県輪島市の伝統工芸・輪島塗を記録したドキュメンタリー写真プロジェクト。300年続く職人の手仕事と工房の空気を写真と映像で伝えます。',
      url: `${SITE_URL}/portfolio/wajima-nuri`,
      inLanguage: 'ja',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Projects', item: `${SITE_URL}/#projects` },
          { '@type': 'ListItem', position: 3, name: '輪島塗', item: `${SITE_URL}/portfolio/wajima-nuri` },
        ],
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'jsonld-wajima-nuri';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('jsonld-wajima-nuri');
      if (existing) existing.parentNode?.removeChild(existing);
    };
  }, []);

  return null;
}

const WajimaNuriProject = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const galleryImages = [
    {
      id: 1,
      url: 'https://public.readdy.ai/ai/img_res/3dc5c96ae230a6393e22ac0f9d8b7a3a.jpg',
      alt: '漆を塗る職人の手元',
      size: 'medium'
    },
    {
      id: 2,
      url: 'https://readdy.ai/api/search-image?query=Elderly%20Japanese%20master%20craftsman%20concentrating%20on%20traditional%20Wajima%20lacquerware%20in%20wooden%20workshop%20surrounded%20by%20tools%20and%20materials%20soft%20window%20light%20illuminating%20workspace%20documentary%20style%20authentic%20atmosphere%20warm%20earth%20tones%20capturing%20dedication%20and%20craftsmanship&width=800&height=1000&seq=wajima2&orientation=portrait',
      alt: '輪島塗職人の横顔',
      size: 'large'
    },
    {
      id: 3,
      url: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20traditional%20Japanese%20lacquerware%20bowls%20and%20plates%20showing%20glossy%20black%20and%20red%20urushi%20finish%20with%20gold%20makie%20decoration%20arranged%20on%20wooden%20surface%20in%20natural%20light%20documentary%20photography%20shallow%20focus%20rich%20textures%20traditional%20craftsmanship%20details&width=800&height=1000&seq=wajima3&orientation=portrait',
      alt: '完成した輪島塗の器',
      size: 'medium'
    },
    {
      id: 4,
      url: 'https://readdy.ai/api/search-image?query=Traditional%20Japanese%20workshop%20interior%20with%20wooden%20shelves%20filled%20with%20lacquerware%20pieces%20in%20various%20stages%20of%20completion%20natural%20light%20from%20paper%20windows%20creating%20soft%20shadows%20documentary%20style%20peaceful%20atmosphere%20earth%20tones%20showing%20traditional%20craft%20environment&width=1200&height=800&seq=wajima4&orientation=landscape',
      alt: '工房の風景',
      size: 'wide'
    },
    {
      id: 5,
      url: 'https://readdy.ai/api/search-image?query=Hands%20of%20Japanese%20craftsman%20applying%20delicate%20gold%20makie%20decoration%20to%20black%20lacquerware%20using%20fine%20brush%20extreme%20close%20up%20showing%20precision%20and%20skill%20soft%20natural%20lighting%20documentary%20photography%20muted%20colors%20focusing%20on%20intricate%20traditional%20technique&width=800&height=1000&seq=wajima5&orientation=portrait',
      alt: '蒔絵の作業',
      size: 'medium'
    },
    {
      id: 6,
      url: 'https://readdy.ai/api/search-image?query=Raw%20wooden%20base%20materials%20for%20lacquerware%20alongside%20finished%20glossy%20black%20urushi%20pieces%20showing%20transformation%20process%20arranged%20on%20traditional%20Japanese%20wooden%20table%20natural%20window%20light%20documentary%20style%20earth%20tones%20emphasizing%20craft%20journey%20from%20wood%20to%20art&width=800&height=1000&seq=wajima6&orientation=portrait',
      alt: '木地から完成まで',
      size: 'medium'
    },
    {
      id: 7,
      url: 'https://readdy.ai/api/search-image?query=Traditional%20Japanese%20lacquerware%20tools%20including%20brushes%20spatulas%20and%20urushi%20containers%20arranged%20on%20aged%20wooden%20workbench%20in%20craftsman%20workshop%20soft%20natural%20light%20documentary%20photography%20warm%20tones%20showing%20well%20used%20tools%20of%20traditional%20craft&width=800&height=1000&seq=wajima7&orientation=portrait',
      alt: '職人の道具',
      size: 'medium'
    },
    {
      id: 8,
      url: 'https://readdy.ai/api/search-image?query=Japanese%20craftsman%20carefully%20inspecting%20finished%20lacquerware%20piece%20holding%20it%20up%20to%20natural%20light%20in%20traditional%20workshop%20showing%20dedication%20to%20quality%20documentary%20style%20soft%20lighting%20muted%20colors%20capturing%20moment%20of%20craftsmanship%20pride%20and%20attention%20to%20detail&width=800&height=1000&seq=wajima8&orientation=portrait',
      alt: '仕上がりを確認する職人',
      size: 'medium'
    }
  ];

  const handleNavigate = (path: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(path);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <WajimaNuriJsonLd />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
          <button
            onClick={() => handleNavigate('/')}
            className="text-stone-800 hover:text-stone-600 transition-colors cursor-pointer font-light tracking-wide"
          >
            Kazuki Odake
          </button>
          <button
            onClick={() => handleNavigate('/')}
            className="text-stone-600 hover:text-stone-800 transition-colors cursor-pointer flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            <span className="text-sm">Back to Home</span>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="w-full h-[70vh] overflow-hidden rounded-2xl mb-16">
            <img
              src="https://readdy.ai/api/search-image?query=Atmospheric%20wide%20shot%20of%20elderly%20Japanese%20master%20craftsman%20working%20on%20traditional%20Wajima%20lacquerware%20in%20sunlit%20wooden%20workshop%20with%20tools%20and%20materials%20surrounding%20him%20soft%20natural%20light%20creating%20dramatic%20shadows%20documentary%20cinematic%20style%20warm%20earth%20tones%20capturing%20essence%20of%20300%20year%20old%20tradition%20and%20dedication&width=1600&height=900&seq=wajimahero&orientation=landscape"
              alt="輪島塗 - 職人の手仕事"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="max-w-3xl">
            <h1 className="text-5xl lg:text-6xl font-light text-stone-800 mb-6 tracking-tight">
              輪島塗
            </h1>
            <p className="text-2xl text-stone-600 mb-8 font-light">
              300年続く手仕事
            </p>
            <p className="text-base lg:text-lg text-stone-600 leading-relaxed font-light">
              石川県輪島市で受け継がれてきた伝統工芸。<br />
              分業によって支えられる技術と、職人たちの静かな手仕事を記録しています。
            </p>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-2xl lg:text-3xl text-stone-700 leading-relaxed font-light tracking-wide">
            ひとつの器の裏側には、いくつもの手仕事がある。
          </p>
        </div>
      </section>

      {/* Project Introduction */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <p className="text-base lg:text-lg text-stone-700 leading-relaxed font-light">
            輪島塗は、木地づくり、漆塗り、蒔絵など多くの工程を経て完成します。<br />
            それぞれの職人が専門の技術を持ち、長い時間をかけて一つの作品を作り上げます。<br />
            このページでは、その手仕事と工房の空気を写真と映像で記録しています。
          </p>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-10">
            {/* First Row - Two medium images */}
            <div className="md:col-span-6 group cursor-pointer">
              <div className="aspect-[4/5] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[0].url}
                  alt={galleryImages[0].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>
            <div className="md:col-span-6 group cursor-pointer">
              <div className="aspect-[4/5] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[2].url}
                  alt={galleryImages[2].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Second Row - One large portrait image */}
            <div className="md:col-span-12 group cursor-pointer">
              <div className="aspect-[16/10] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[1].url}
                  alt={galleryImages[1].alt}
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Third Row - Wide landscape image */}
            <div className="md:col-span-12 group cursor-pointer">
              <div className="aspect-[3/2] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[3].url}
                  alt={galleryImages[3].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Fourth Row - Three medium images */}
            <div className="md:col-span-4 group cursor-pointer">
              <div className="aspect-[4/5] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[4].url}
                  alt={galleryImages[4].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>
            <div className="md:col-span-4 group cursor-pointer">
              <div className="aspect-[4/5] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[5].url}
                  alt={galleryImages[5].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>
            <div className="md:col-span-4 group cursor-pointer">
              <div className="aspect-[4/5] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[6].url}
                  alt={galleryImages[6].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Fifth Row - One medium image */}
            <div className="md:col-span-6 md:col-start-4 group cursor-pointer">
              <div className="aspect-[4/5] overflow-hidden rounded-lg bg-stone-200">
                <img
                  src={galleryImages[7].url}
                  alt={galleryImages[7].alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Documentary Film Section */}
      <section className="py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-2">
              <h2 className="text-3xl lg:text-4xl font-light text-stone-800 mb-6 tracking-tight">
                Documentary Film
              </h2>
              <p className="text-base text-stone-600 font-light leading-relaxed">
                輪島塗の手仕事と工房の空気を、映像でも記録しています。
              </p>
            </div>
            <div className="lg:col-span-3 aspect-video rounded-2xl overflow-hidden bg-stone-200">
              <div className="w-full h-full relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Cinematic%20still%20frame%20from%20documentary%20film%20showing%20Japanese%20craftsman%20working%20on%20traditional%20Wajima%20lacquerware%20in%20atmospheric%20workshop%20with%20soft%20natural%20light%20documentary%20photography%20style%20warm%20muted%20tones%20capturing%20quiet%20dedication%20and%20traditional%20craftsmanship%20essence&width=1200&height=675&seq=wajima-film-thumb&orientation=landscape"
                  alt="Documentary Film"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                    <i className="ri-play-fill text-2xl text-stone-800 ml-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Information */}
      <section className="py-32 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center md:text-left">
            <div>
              <p className="text-xs text-stone-500 mb-3 uppercase tracking-wider font-light">
                場所
              </p>
              <p className="text-base text-stone-800 font-light">
                石川県 輪島市
              </p>
            </div>
            <div>
              <p className="text-xs text-stone-500 mb-3 uppercase tracking-wider font-light">
                種別
              </p>
              <p className="text-base text-stone-800 font-light">
                Documentary Photography
              </p>
            </div>
            <div>
              <p className="text-xs text-stone-500 mb-3 uppercase tracking-wider font-light">
                主題
              </p>
              <p className="text-base text-stone-800 font-light">
                伝統工芸 / 手仕事 / 文化記録
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section className="py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-2xl font-light text-stone-800 mb-16 tracking-tight text-center">
            関連プロジェクト
          </h3>
          <div
            onClick={() => handleNavigate('/portfolio/noto-recovery')}
            className="group cursor-pointer rounded-2xl overflow-hidden bg-stone-50 hover:shadow-lg transition-all duration-500"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Hopeful%20scene%20of%20Noto%20Peninsula%20recovery%20showing%20community%20members%20working%20together%20rebuilding%20traditional%20Japanese%20buildings%20with%20coastal%20landscape%20in%20background%20documentary%20photography%20natural%20light%20warm%20tones%20capturing%20resilience%20and%20hope%20after%20disaster&width=800&height=600&seq=notorelated&orientation=landscape"
                  alt="Recover Noto"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-12 lg:p-16 flex flex-col justify-center">
                <h4 className="text-2xl font-light text-stone-800 mb-6 tracking-tight">
                  Recover Noto
                </h4>
                <p className="text-sm text-stone-600 mb-8 font-light leading-relaxed">
                  能登の復興、海藻、手仕事をめぐる<br />
                  進行中のドキュメンタリープロジェクト。
                </p>
                <div className="flex items-center gap-2 text-stone-800 group-hover:gap-4 transition-all">
                  <span className="text-sm font-light whitespace-nowrap">View Project</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-100 py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-sm text-stone-600 font-light">
              © 2024 Kazuki Odake Photography. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-600 hover:text-stone-800 transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-xl" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-stone-600 hover:text-stone-800 transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-line text-xl" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WajimaNuriProject;